import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dictée vocale via l'API Web Speech du navigateur (gratuite, aucun appel serveur).
 * Chrome / Edge / Safari ; absente de Firefox → `supported` est faux et l'appelant
 * masque simplement le bouton. Chrome transmet l'audio à Google pour la transcription.
 */
const SpeechRecognitionCtor: any =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : undefined;

export const dictationSupported = !!SpeechRecognitionCtor;

function joinWithBase(base: string, transcript: string) {
  if (!transcript) return base;
  if (!base) return transcript;
  return /\s$/.test(base) ? base + transcript : base + ' ' + transcript;
}

export function useDictation({ lang = 'fr-FR', onText }: { lang?: string; onText: (text: string) => void }) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<any>(null);
  const baseRef = useRef('');       // texte déjà présent dans le champ quand la dictée démarre
  const finalRef = useRef('');      // segments définitifs accumulés depuis le démarrage
  const wantRef = useRef(false);    // l'utilisateur veut-il toujours dicter ?
  const restartsRef = useRef<number[]>([]);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = useCallback(() => {
    wantRef.current = false;
    setListening(false);
    try { recRef.current?.stop(); } catch { /* déjà arrêtée */ }
  }, []);

  const start = useCallback(
    (base: string) => {
      if (!SpeechRecognitionCtor) return;
      setError(null);
      baseRef.current = base;
      finalRef.current = '';
      restartsRef.current = [];
      wantRef.current = true;

      const rec = new SpeechRecognitionCtor();
      recRef.current = rec;
      rec.lang = lang;
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      rec.onresult = (e: any) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const chunk = e.results[i][0]?.transcript ?? '';
          if (e.results[i].isFinal) {
            finalRef.current = joinWithBase(finalRef.current, chunk.trim());
          } else {
            interim += chunk;
          }
        }
        onTextRef.current(joinWithBase(baseRef.current, joinWithBase(finalRef.current, interim.trim())));
      };

      rec.onerror = (e: any) => {
        // 'no-speech' et 'aborted' sont des fins de session normales : onend relancera.
        if (e.error === 'no-speech' || e.error === 'aborted') return;
        wantRef.current = false;
        setListening(false);
        setError(
          e.error === 'not-allowed' || e.error === 'service-not-allowed'
            ? 'Micro refusé — autorise l\'accès au microphone dans le navigateur.'
            : e.error === 'network'
              ? 'Service de reconnaissance vocale injoignable.'
              : 'Dictée interrompue (' + e.error + ').',
        );
      };

      // Chrome coupe la session après quelques secondes de silence, même en `continuous`.
      // On relance tant que l'utilisateur n'a pas cliqué sur stop, avec un garde-fou
      // contre la boucle de redémarrage quand start() échoue immédiatement.
      rec.onend = () => {
        if (!wantRef.current) { setListening(false); return; }
        const now = Date.now();
        restartsRef.current = restartsRef.current.filter((t) => now - t < 10_000);
        if (restartsRef.current.length >= 5) {
          wantRef.current = false;
          setListening(false);
          setError('Dictée arrêtée : le navigateur coupe la session en boucle.');
          return;
        }
        restartsRef.current.push(now);
        // Le texte déjà dicté devient la base du prochain segment.
        baseRef.current = joinWithBase(baseRef.current, finalRef.current);
        finalRef.current = '';
        try { rec.start(); } catch { setListening(false); wantRef.current = false; }
      };

      try {
        rec.start();
        setListening(true);
      } catch {
        setListening(false);
        wantRef.current = false;
        setError('Impossible de démarrer la dictée.');
      }
    },
    [lang],
  );

  const toggle = useCallback((base: string) => { (listening ? stop : () => start(base))(); }, [listening, start, stop]);

  useEffect(() => () => { wantRef.current = false; try { recRef.current?.abort(); } catch { /* rien */ } }, []);

  return { supported: dictationSupported, listening, error, start, stop, toggle };
}
